import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CommentForm } from './CommentForm';
import type { BlogComment } from '@/types/blog';

interface CommentProps {
  comment: BlogComment;
  currentUserId?: string;
  onReply: (content: string, parentId: string) => void;
  onDelete: (commentId: string) => void;
  isSubmitting?: boolean;
  depth?: number;
}

export function Comment({
  comment,
  currentUserId,
  onReply,
  onDelete,
  isSubmitting,
  depth = 0,
}: CommentProps) {
  const { t } = useTranslation();
  const [showReply, setShowReply] = useState(false);
  const maxDepth = 3;

  return (
    <div className={`${depth > 0 ? 'ml-6 border-l-2 border-emerald-100 pl-4' : ''}`}>
      <div className="flex items-start gap-3 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-medium text-emerald-700">
          {comment.userName?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium">{comment.userName}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(comment.createdAt).toLocaleDateString()}
            </span>
          </div>
          <p className="text-sm text-foreground/80 whitespace-pre-wrap">{comment.content}</p>
          <div className="mt-2 flex items-center gap-2">
            {depth < maxDepth && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setShowReply(!showReply)}
              >
                <MessageCircle className="h-3 w-3" />
                {t('blogPage.reply')}
              </Button>
            )}
            {currentUserId === comment.userId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
                onClick={() => onDelete(comment.id)}
              >
                <Trash2 className="h-3 w-3" />
                {t('common.delete')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {showReply && (
        <div className="ml-11 mb-3">
          <CommentForm
            onSubmit={(content) => {
              onReply(content, comment.id);
              setShowReply(false);
            }}
            isSubmitting={isSubmitting}
            placeholder={t('blogPage.replyPlaceholder')}
            onCancel={() => setShowReply(false)}
          />
        </div>
      )}

      {comment.replies?.map((reply) => (
        <Comment
          key={reply.id}
          comment={reply}
          currentUserId={currentUserId}
          onReply={onReply}
          onDelete={onDelete}
          isSubmitting={isSubmitting}
          depth={depth + 1}
        />
      ))}
    </div>
  );
}
